// Theme Manager Hook - Modern theme management without context
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { useState, useCallback, useMemo, useEffect } from "react";

import { PaletteMode } from "@mui/material";

import { StorageKeys } from "../constants/Storage";

/**
 * Get system theme preference
 */
const getSystemTheme = (): PaletteMode => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * Theme management hook that replaces the legacy ThemeContext
 * Supports system preference detection and localStorage persistence
 */
export const useThemeManager = () => {
  // Initialize theme mode from localStorage, system preference, or default to 'light'
  const [mode, setMode] = useState<PaletteMode>(() => {
    // Priority: localStorage > system preference > light
    const savedMode = localStorage.getItem(StorageKeys.THEME_MODE);
    if (savedMode === "light" || savedMode === "dark") {
      return savedMode;
    }
    // Fall back to system preference
    return getSystemTheme();
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't set a preference
      const savedMode = localStorage.getItem(StorageKeys.THEME_MODE);
      if (!savedMode) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    // Add listener (with fallback for older browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const toggleColorMode = useCallback(() => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem(StorageKeys.THEME_MODE, newMode);
  }, [mode]);

  const setColorMode = useCallback((newMode: PaletteMode) => {
    setMode(newMode);
    localStorage.setItem(StorageKeys.THEME_MODE, newMode);
  }, []);

  const themeInfo = useMemo(
    () => ({
      mode,
      toggleColorMode,
      setColorMode,
      isDark: mode === "dark",
      isLight: mode === "light",
    }),
    [mode, toggleColorMode, setColorMode],
  );

  return themeInfo;
};

/**
 * Get initial theme mode (for SSR or initial render)
 * @deprecated Use useThemeManager hook instead
 */
export const getInitialThemeMode = (): PaletteMode => {
  const savedMode = localStorage?.getItem(StorageKeys.THEME_MODE);
  if (savedMode === "light" || savedMode === "dark") {
    return savedMode;
  }
  return getSystemTheme();
};
